import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NOOR } from "@/constants/testIds/noor";
import { overlayTrans } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PANEL_Y_OFFSET = 30;
const PANEL_DURATION = 0.8;
const PANEL_DELAY = 0.15;
const EASE_OUT_EXPO = [0.22, 1, 0.36, 1];

const OVERLAY = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const PANEL = {
  initial: { y: PANEL_Y_OFFSET, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { delay: PANEL_DELAY, duration: PANEL_DURATION, ease: EASE_OUT_EXPO },
};

async function fetchAgeStatus() {
  const res = await fetch(`${API}/legal/age-status`, { credentials: "include" });
  if (!res.ok) return false;
  const data = await res.json();
  return Boolean(data?.verified);
}

async function postAgeVerify() {
  const res = await fetch(`${API}/legal/age-verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  return res.ok;
}

function NoorMark() {
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="27" stroke="#C9A47A" strokeWidth="0.6" />
      <circle cx="28" cy="28" r="22" stroke="#C9A47A" strokeWidth="0.4" opacity="0.5" />
      <text x="50%" y="55%" textAnchor="middle" fill="#C9A47A" fontFamily="Fraunces, serif" fontSize="22" fontWeight="500">N</text>
    </svg>
  );
}

function AgeGatePanel({ onAccept, onDecline, busy }) {
  return (
    <motion.div {...PANEL} className="max-w-xl w-full mx-6 text-center px-8 py-14 relative">
      <div className="mb-8 flex justify-center">
        <NoorMark />
      </div>
      <p className="overline mb-6">Verification of Age</p>
      <h2 className="font-display text-4xl md:text-5xl leading-tight mb-6" style={{ color: "var(--noor-bone)" }}>
        Are you of <em className="italic" style={{ color: "var(--noor-rosegold)" }}>legal drinking</em> age?
      </h2>
      <p className="text-sm md:text-base mb-10" style={{ color: "var(--noor-vellum)" }}>
        You must be of legal drinking age — <span style={{ color: "var(--noor-rosegold)" }}>21+ in the United States</span>, <span style={{ color: "var(--noor-rosegold)" }}>19+ in most other markets</span> — to enter the house of NOOR. Please drink responsibly.
      </p>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
        <button
          data-testid={NOOR.ageGateYes}
          onClick={onAccept}
          disabled={busy}
          className="btn-solid"
        >
          {busy ? "Verifying…" : "I am 21 or older"}
        </button>
        <button
          data-testid="age-gate-yes-19"
          onClick={onAccept}
          disabled={busy}
          className="btn-ghost"
          style={{ borderColor: "var(--noor-rosegold)" }}
        >
          I am 19 or older
        </button>
        <button
          data-testid={NOOR.ageGateNo}
          onClick={onDecline}
          disabled={busy}
          className="btn-ghost"
        >
          I am underage
        </button>
      </div>
      <p className="mt-10 font-mono text-[10px]" style={{ color: "var(--noor-vellum)", letterSpacing: "0.3em", textTransform: "uppercase" }}>
        By entering you accept our terms & privacy policy
      </p>
    </motion.div>
  );
}

export default function AgeGate() {
  // `open` is undefined until we hear from the server. We never flash the gate
  // for users who already verified — the overlay only mounts after the check.
  const [open, setOpen] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAgeStatus().then((verified) => {
      if (!cancelled) setOpen(!verified);
    }).catch(() => {
      // Network error → fail closed (show the gate) so the legal requirement is preserved.
      if (!cancelled) setOpen(true);
    });
    return () => { cancelled = true; };
  }, []);

  const accept = useCallback(async () => {
    setBusy(true);
    const ok = await postAgeVerify().catch(() => false);
    setBusy(false);
    if (ok) setOpen(false);
  }, []);

  const decline = useCallback(() => {
    window.location.href = "https://www.google.com";
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={NOOR.ageGate}
          {...OVERLAY}
          transition={overlayTrans}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "var(--noor-obsidian)" }}
        >
          <div className="grain-overlay" />
          <AgeGatePanel onAccept={accept} onDecline={decline} busy={busy} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
